/**
 * The two warnings the New Lead / New Job modal can show before it saves.
 * Both ask and neither blocks: the rep can always go ahead.
 */

import {
  formatAddress,
  jobAddress,
  STAGE_LABEL,
  type Contact,
  type Job,
} from "@roof-hawk/core";

import { t, tn } from "../../i18n";

/** This customer may already be in the system (ADR 0007). */
export function DuplicateWarning({
  duplicates,
  jobCountFor,
  onUse,
  onDifferentPerson,
}: {
  duplicates: readonly Contact[];
  jobCountFor: (contactId: string) => number;
  onUse: (contact: Contact) => void;
  onDifferentPerson: () => void;
}) {
  return (
    <div className="dupwarn">
      <b>{t("Possible duplicate")}</b> —{" "}
      {t("this customer may already be in the system:")}
      {duplicates.slice(0, 3).map((dupe) => (
        <div key={dupe.id} className="dupitem" onClick={() => onUse(dupe)}>
          <span>
            <b>{dupe.full_name}</b> · {formatAddress(dupe) || t("no address")} ·{" "}
            {dupe.cell ?? dupe.phone ?? t("no phone")} ·{" "}
            {tn(jobCountFor(dupe.id), "1 job", "{n} jobs")}
          </span>
          <span className="use">{t("Add job to this customer")}</span>
        </div>
      ))}
      <span className="chg" onClick={onDifferentPerson}>
        {t("No, this is a different person — create a new customer")}
      </span>
    </div>
  );
}

/**
 * "Are you sure?" before a customer gets another job while one is still going
 * (ADR 0272). It names the job they already have and offers it instead.
 */
export function OpenJobsWarning({
  customerName,
  jobs,
  onOpenJob,
  onConfirm,
}: {
  customerName: string | null;
  jobs: readonly Job[];
  /** The link already goes to the job; this closes the modal over it. */
  onOpenJob: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="dupwarn" role="alert">
      <b>{t("Are you sure?")}</b>{" "}
      {tn(
        jobs.length,
        "{name} already has a job still open. Do you want to create another job for them?",
        "{name} already has {n} jobs still open. Do you want to create another job for them?",
        { name: customerName || t("This customer") },
      )}
      {jobs.slice(0, 3).map((job) => (
        <div key={job.id} className="dupitem">
          <span>
            <b>#{job.job_number}</b> · {t(STAGE_LABEL[job.stage])} ·{" "}
            {jobAddress(job) || t("no address")}
          </span>
          <a className="use" href={`#job=${job.id}`} onClick={onOpenJob}>
            {t("Open this job")}
          </a>
        </div>
      ))}
      <span className="chg" onClick={onConfirm}>
        {t("Yes, create another job")}
      </span>
    </div>
  );
}
